/**
 * PostHog provider.
 *
 * Decides, on every route change and every change of answer, whether
 * analytics may run (consent.ts analyticsAllowed), and starts or stops it.
 * While it runs it sends sanitised pageviews, the account id, and the active
 * project's slug. While it does not, nothing here sends anything.
 */

import { createContext, useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { clearAnalyticsStorage, groupProject, identifyUser, posthog, startAnalytics, stopAnalytics } from './posthog';
import { analyticsAllowed, hasGlobalPrivacyControl, sanitizePath, sanitizeUrl, useConsentStore } from './consent';
import { useGlobalFrustrationDetector } from './use-frustration-detector';
import { useSessionStore } from '../auth/session';
import { useDemoStore } from '../mock/store';
import { useProjectSlug } from '../hooks/use-project-slug';

const PostHogContext = createContext<typeof posthog | null>(null);

const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

/** The inputs to analyticsAllowed, read from the live stores. Shared with ConsentNotice. */
export function useAnalyticsGate() {
  const location = useLocation();
  const choice = useConsentStore((s) => s.choice);
  const account = useSessionStore((s) => s.account);
  const sessionStatus = useSessionStore((s) => s.status);
  const impersonation = useSessionStore((s) => s.impersonation);
  return {
    choice,
    gpc: hasGlobalPrivacyControl(),
    // Unknown while the session loads counts as ineligible, so a student's
    // first page in the console never sends anything before the answer arrives.
    accountEligible:
      USE_MOCK || sessionStatus === 'signed-out'
        ? undefined
        : sessionStatus === 'loading'
          ? false
          : account?.analyticsEligible,
    impersonating: impersonation !== null,
    pathname: location.pathname,
  };
}

export function PostHogProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const activeSlug = useProjectSlug();
  const gate = useAnalyticsGate();
  const allowed = analyticsAllowed(gate);

  const account = useSessionStore((s) => s.account);
  const sessionStatus = useSessionStore((s) => s.status);
  const demoAccount = useDemoStore((s) => s.account);
  const prefs = useDemoStore((s) => s.prefs);

  useEffect(() => {
    if (allowed) startAnalytics();
    else stopAnalytics();
  }, [allowed]);

  useEffect(() => {
    if (gate.choice !== 'granted' || gate.gpc) clearAnalyticsStorage();
  }, [gate.choice, gate.gpc]);

  useEffect(() => {
    if (!allowed) return;
    posthog.capture('$pageview', {
      $current_url: sanitizeUrl(window.location.href),
      $pathname: sanitizePath(location.pathname),
      $title: document.title,
      project_slug: activeSlug,
    });
  }, [allowed, location.pathname, activeSlug]);

  useEffect(() => {
    if (!allowed) return;
    const a = !USE_MOCK && sessionStatus === 'signed-in' ? account : USE_MOCK ? demoAccount : undefined;
    if (!a) return;
    identifyUser({
      id: a.id,
      plan: a.plan,
      detailLevel: a.detailLevel,
      createdAt: a.createdAt,
      isAdmin: a.isAdmin,
      theme: prefs.theme,
      direction: prefs.direction,
      lang: prefs.lang,
      apiMode: USE_MOCK ? 'mock' : 'real',
    });
  }, [allowed, account, sessionStatus, demoAccount, prefs.theme, prefs.direction, prefs.lang]);

  useEffect(() => {
    if (allowed && activeSlug) groupProject(activeSlug);
  }, [allowed, activeSlug]);

  useGlobalFrustrationDetector();

  return <PostHogContext.Provider value={posthog}>{children}</PostHogContext.Provider>;
}
